import { DAP_BRAND_URLS, DAP_SANITY_CONFIG } from '@dap/common/consts';
import { brandStylesConfig } from '@dap/common/styles';
import { Env, EnvConfigs } from '@dap/common/types';
import {
  bademiljoeFeaturesDev,
  bademiljoeFeaturesProd,
  bademiljoeFeaturesStage,
  blinkhusFeaturesDev,
  blinkhusFeaturesProd,
  blinkhusFeaturesStage,
  boligpartnerFeaturesDev,
  boligpartnerFeaturesProd,
  boligpartnerFeaturesStage,
  bolistFeaturesDev,
  bolistFeaturesProd,
  bolistFeaturesStage,
  byggtorgetFeaturesDev,
  byggtorgetFeaturesProd,
  byggtorgetFeaturesStage,
  comfortFeaturesDev,
  comfortFeaturesProd,
  comfortFeaturesStage,
  defaultBrandFeatures,
  fargerikeBrandFeaturesDev,
  fargerikeBrandFeaturesProd,
  fargerikeBrandFeaturesStage,
  kuloerBrandFeaturesDev,
  kuloerBrandFeaturesProd,
  kuloerBrandFeaturesStage,
  mestergruppenFeaturesDev,
  mestergruppenFeaturesProd,
  mestergruppenFeaturesStage,
  mesterhusFeaturesDev,
  mesterhusFeaturesProd,
  mesterhusFeaturesStage,
  nordbohusFeaturesDev,
  nordbohusFeaturesProd,
  nordbohusFeaturesStage,
  pretreFeaturesDev,
  pretreFeaturesProd,
  pretreFeaturesStage,
  saltdalshyttaBrandFeaturesDev,
  saltdalshyttaBrandFeaturesProd,
  saltdalshyttaBrandFeaturesStage,
  systemhusFeaturesDev,
  systemhusFeaturesProd,
  systemhusFeaturesStage,
  wifaBrandFeaturesDev,
  wifaBrandFeaturesProd,
  wifaBrandFeaturesStage,
  xlbyggFeaturesDev,
  xlbyggFeaturesProd,
  xlbyggFeaturesStage,
  xlbyggSverigeFeaturesDev,
  xlbyggSverigeFeaturesProd,
  xlbyggSverigeFeaturesStage,
} from '@dap/dashboard/config';

export const env: Env =
  (process.env['NX_PUBLIC_REACT_APP_ENV'] as 'dev' | 'stage' | 'prod') || 'local';

const sentryDsn = 'https://5d8fa7a1af194ea9bef381dc2a8c5350@o344472.ingest.sentry.io/6642861';

const muiLicenseKey =
  'bebbb7e316354130b2d89b644169cd7bTz0xMjEzMjEsRT0xNzkzODM2Nzk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y';

const appConfig = (version: string): EnvConfigs => ({
  dev: {
    features: {
      brandFeatures: {
        defaultFeatures: defaultBrandFeatures,
        bademiljoe: bademiljoeFeaturesDev,
        blinkhus: blinkhusFeaturesDev,
        boligpartner: boligpartnerFeaturesDev,
        bolist: bolistFeaturesDev,
        byggtorget: byggtorgetFeaturesDev,
        comfort: comfortFeaturesDev,
        fargerike: fargerikeBrandFeaturesDev,
        kuloer: kuloerBrandFeaturesDev,
        mestergruppen: mestergruppenFeaturesDev,
        mesterhus: mesterhusFeaturesDev,
        nordbohus: nordbohusFeaturesDev,
        pretre: pretreFeaturesDev,
        saltdalshytta: saltdalshyttaBrandFeaturesDev,
        systemhus: systemhusFeaturesDev,
        wifa: wifaBrandFeaturesDev,
        xlbygg: xlbyggFeaturesDev,
        xlbyggsverige: xlbyggSverigeFeaturesDev,
      },
    },
    styles: brandStylesConfig,
    auth0: {
      clientId: 'mcsF7fJ3Ir5F2zH1W6Y5jGL7Vtpx9sH4',
      domain: 'dev.id.mestergruppen.cloud',
      audience: 'mg.iam',
    },
    iam: { url: 'https://iam.datahub-dev.mestergruppen.cloud' },
    version,
    gaMeasurementId: 'G-8T4C592GCE',
    oldAppLink: 'https://dev.kjedeadmin.mestergruppen.cloud',
    sanity: {
      ...DAP_SANITY_CONFIG,
    },
    sentryDsn,
    muiLicenseKey,
    externalSanityStudios: {
      // Add a test studio for the mesterhus brand, to verify that resolving external sanity studios works as expected
      mesterhus: {
        title: 'mesterhus.no (test)',
        url: 'https://example.com/mesterhus',
      },
    },
  },
  stage: {
    features: {
      brandFeatures: {
        defaultFeatures: defaultBrandFeatures,
        bademiljoe: bademiljoeFeaturesStage,
        blinkhus: blinkhusFeaturesStage,
        boligpartner: boligpartnerFeaturesStage,
        bolist: bolistFeaturesStage,
        byggtorget: byggtorgetFeaturesStage,
        comfort: comfortFeaturesStage,
        fargerike: fargerikeBrandFeaturesStage,
        kuloer: kuloerBrandFeaturesStage,
        mestergruppen: mestergruppenFeaturesStage,
        mesterhus: mesterhusFeaturesStage,
        nordbohus: nordbohusFeaturesStage,
        pretre: pretreFeaturesStage,
        saltdalshytta: saltdalshyttaBrandFeaturesStage,
        systemhus: systemhusFeaturesStage,
        wifa: wifaBrandFeaturesStage,
        xlbygg: xlbyggFeaturesStage,
        xlbyggsverige: xlbyggSverigeFeaturesStage,
      },
    },
    styles: brandStylesConfig,
    auth0: {
      clientId: 'TdYKUtMDSKB1mI02IfbHkF26LmZm06HL',
      domain: 'stage.id.mestergruppen.cloud',
      audience: 'mg.iam',
    },
    iam: { url: 'https://iam.datahub-stage.mestergruppen.cloud' },
    version,
    gaMeasurementId: 'G-0Q552BVJPV',
    oldAppLink: 'https://stage.kjedeadmin.mestergruppen.cloud',
    sanity: DAP_SANITY_CONFIG,
    sentryDsn,
    muiLicenseKey,
    externalSanityStudios: {
      // No need to link to stage for any current external sanity studios, as access is not controlled with roles from Mester ID, only regular Sanity accounts
    },
  },
  prod: {
    features: {
      brandFeatures: {
        defaultFeatures: {
          ...defaultBrandFeatures,
          completeRegistrationEmailInfo: { resolveToggle: () => 'off' },
        },
        bademiljoe: bademiljoeFeaturesProd,
        blinkhus: blinkhusFeaturesProd,
        boligpartner: boligpartnerFeaturesProd,
        bolist: bolistFeaturesProd,
        byggtorget: byggtorgetFeaturesProd,
        comfort: comfortFeaturesProd,
        fargerike: fargerikeBrandFeaturesProd,
        kuloer: kuloerBrandFeaturesProd,
        mestergruppen: mestergruppenFeaturesProd,
        mesterhus: mesterhusFeaturesProd,
        nordbohus: nordbohusFeaturesProd,
        pretre: pretreFeaturesProd,
        saltdalshytta: saltdalshyttaBrandFeaturesProd,
        systemhus: systemhusFeaturesProd,
        wifa: wifaBrandFeaturesProd,
        xlbygg: xlbyggFeaturesProd,
        xlbyggsverige: xlbyggSverigeFeaturesProd,
      },
    },
    styles: brandStylesConfig,
    auth0: {
      clientId: 'viR4pX7HxSMhstP8Y5KohMBpYf225AEG',
      domain: 'id.mestergruppen.cloud',
      audience: 'mg.iam',
    },
    iam: { url: 'https://iam.datahub-prod.mestergruppen.cloud' },
    version,
    gaMeasurementId: 'G-Q1DQPQ544P',
    // hotjar: {
    //   id: 3217717,
    //   version: 6,
    // },
    clarity: {
      id: 'jdh67vcatk',
    },
    oldAppLink: 'https://kjedeadmin.mestergruppen.cloud',
    sanity: DAP_SANITY_CONFIG,
    sentryDsn,
    dapBrandUrls: DAP_BRAND_URLS,
    muiLicenseKey,
    externalSanityStudios: {
      bademiljoe: {
        title: 'bademiljo.no',
        url: 'https://bademiljo.no/studio',
      },
      blinkhus: {
        title: 'blink-hus.no',
        url: 'https://blink-hus.no/studio',
      },
      comfort: {
        title: 'comfort.no',
        url: 'https://comfort.no/studio',
      },
      mesterhus: {
        title: 'mesterhus.no',
        url: 'https://mesterhus.no/studio',
      },
      nordbohus: {
        title: 'nordbohus.no',
        url: 'https://nordbohus.no/studio',
      },
      systemhus: {
        title: 'systemhus.no',
        url: 'https://systemhus.no/studio',
      },
    },
  },
});

const getConfig = (env: Env) => {
  const version = process.env['NX_PUBLIC_REACT_APP_VERSION'] || 'local';
  return env === 'local' ? appConfig(version).dev : appConfig(version)[env];
};

export const config = getConfig(env);
